import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { ScrollReveal, StaggerContainer, StaggerItem } from "@/components/ScrollReveal";
import { AppointmentForm } from "@/components/AppointmentForm";
import { IshiharaTestSection } from "@/components/IshiharaTest";
import { FaqSection } from "@/components/FaqSection";
import { PhoneLink } from "@/components/PhoneLink";
import { ReviewsMarquee } from "@/components/ReviewsMarquee";
import { useAppointment } from "@/components/AppointmentModal";
import {
  IMAGES, SERVICES, DOCTORS, COUNTERS, ADVANTAGES, CONTACT, SELF_TEST_QUESTIONS, LOGO_HORIZONTAL,
} from "@/data/constants";
import { DOCTOR_PAGES } from "@/data/doctorPages";
import {
  Eye, Sparkles, Baby, Glasses, ScanEye, ArrowRight, ArrowDown, Star,
  Award, Cpu, Users, ShieldCheck, Phone, MapPin, Clock, ChevronLeft, ChevronRight,
} from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const iconMap = { Eye, Sparkles, Baby, Glasses, ScanEye, Award, Cpu, Users, ShieldCheck };

// ─── Counter Hook ───
function useCountUp(end, duration = 2000, startCounting = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!startCounting) return;
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [end, duration, startCounting]);
  return count;
}

function formatNumber(n) {
  if (n >= 1000000) return `${(n / 1000000).toFixed(0)} млн`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)} тис`;
  return n.toString();
}

// ─── Hero Section ───
// Slider through all doctors (user-mandated UX). To keep LCP fast:
//   - The FIRST photo (baryska) is preloaded in index.html with imagesrcset
//     and rendered with loading=eager + fetchpriority=high.
//   - Remaining 7 photos are preloaded only AFTER the browser is idle (via
//     requestIdleCallback), so they do not compete with the LCP paint.
const HeroSection = () => {
  const { open: openAppointment } = useAppointment();
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % DOCTORS.length), 5000);
    return () => clearInterval(t);
  }, []);

  // Preload the OTHER 7 doctor photos only when the browser is idle — this
  // prevents them from competing with the LCP (baryska) during initial paint.
  useEffect(() => {
    const preloadRest = () => {
      DOCTORS.slice(1).forEach((d) => {
        const img = new Image();
        img.src = d.photo;
      });
    };
    const ric = window.requestIdleCallback;
    if (typeof ric === "function") {
      const id = ric(preloadRest, { timeout: 4000 });
      return () => window.cancelIdleCallback && window.cancelIdleCallback(id);
    }
    const t = setTimeout(preloadRest, 2500);
    return () => clearTimeout(t);
  }, []);

  const current = DOCTORS[idx];
  return (
    <section className="relative flex flex-col" data-testid="hero-section">
      {/* Main hero content */}
      <div className="bg-[#F5F5F5]">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-32 pb-8 lg:pb-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Left - Text */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              >
                <h1 className="text-4xl sm:text-5xl lg:text-[52px] xl:text-[60px] font-bold text-[#333] leading-[1.1] tracking-tight mb-2 whitespace-nowrap">
                  Повертаємо Ваш зір
                </h1>
                <p className="text-2xl sm:text-3xl lg:text-[32px] font-medium text-[#808080] leading-tight mb-8">
                  професійно та дбайливо
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="flex flex-wrap gap-4"
              >
                <button
                  onClick={openAppointment}
                  className="bg-[#E8590A] text-white px-8 py-4 rounded-full hover:bg-[#D14D08] transition-all duration-300 font-semibold text-base"
                  data-testid="hero-appointment-btn"
                >
                  Записатись на консультацію
                </button>
                <Link
                  to="/diagnostyka"
                  className="bg-white text-[#333] border border-[#333]/20 hover:border-[#333]/40 px-8 py-4 rounded-full transition-all duration-300 font-medium text-base"
                  data-testid="hero-services-btn"
                >
                  Переглянути послуги
                </Link>
              </motion.div>
            </div>

            {/* Right - Doctor photo (slider with crossfade) */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="flex justify-center lg:justify-end"
            >
              <div className="relative">
                {/* Orange accent ring */}
                <div className="absolute -inset-3 rounded-full border-2 border-[#E8590A]/30" />
                <div className="absolute -inset-6 rounded-full border border-[#E8590A]/10" />

                {/* Photo container - oval (crossfade carousel, no gap) */}
                <div className="w-[280px] h-[340px] sm:w-[340px] sm:h-[400px] lg:w-[380px] lg:h-[460px] rounded-full overflow-hidden border-4 border-white shadow-xl relative bg-[#F5F5F5]">
                  {DOCTORS.map((d, i) => (
                    <img
                      key={d.id}
                      src={d.photo}
                      srcSet={i === 0 ? `${d.photoSm} 480w, ${d.photo} 900w` : undefined}
                      sizes={i === 0 ? "(max-width: 640px) 280px, (max-width: 1024px) 340px, 380px" : undefined}
                      alt={`${d.name} — ${d.specialization === "Діагностика" ? "офтальмолог-діагност" : d.specialization === "Дитяча офтальмологія" ? "дитячий офтальмолог" : d.specialization === "Сітківка" ? "офтальмолог, хірург сітківки" : d.specialization === "Хірургія" ? "офтальмолог, хірург катаракти та глаукоми" : "офтальмолог"} у Чернівцях, Центр «Ваш зір»`}
                      width="380"
                      height="460"
                      className="absolute inset-0 w-full h-full object-cover object-[center_15%] transition-opacity duration-700 ease-in-out"
                      style={{ opacity: i === idx ? 1 : 0 }}
                      loading={i === 0 ? "eager" : "lazy"}
                      fetchpriority={i === 0 ? "high" : "auto"}
                      decoding={i === 0 ? "sync" : "async"}
                    />
                  ))}
                </div>

                {/* Decorative orange dot */}
                <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-[#E8590A] rounded-full" />
                <div className="absolute -top-4 -left-4 w-5 h-5 bg-[#E8590A]/40 rounded-full" />
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Stats ticker - removed from hero, now in Layout */}
    </section>
  );
};

// ─── Counters Section ───
const CounterItem = ({ value, suffix, label, delay }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.5 });
  const count = useCountUp(value, 2500, inView);
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ delay, duration: 0.6 }}
      className="text-center"
    >
      <div className="text-4xl sm:text-5xl font-serif text-[#C03A2B] tracking-tight">
        {formatNumber(count)}{suffix}
      </div>
      <div className="mt-2 font-sans text-sm text-[#8C8C8C] uppercase tracking-[0.1em]">{label}</div>
      <motion.div
        initial={{ width: 0 }}
        animate={inView ? { width: "100%" } : {}}
        transition={{ delay: delay + 2, duration: 0.5 }}
        className="h-0.5 bg-[#FAF0EE] mt-4 mx-auto max-w-[60px]"
      />
    </motion.div>
  );
};

const CountersSection = () => (
  <section className="py-20 bg-white" data-testid="counters-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-12">
        {COUNTERS.map((c, i) => (
          <CounterItem key={i} {...c} delay={i * 0.15} />
        ))}
      </div>
    </div>
  </section>
);

// ─── Services Section ───
const HOMEPAGE_SERVICES = [
  { id: "cataract", slug: "katarakta", title: "Лікування катаракти та глаукоми", Icon: Eye },
  { id: "diagnostics", slug: "diagnostyka", title: "Діагностика зору", Icon: ScanEye },
  { id: "presbyopia", slug: "presbyopia", title: "Лікування пресбіопії (вікової далекозорості)", Icon: Glasses },
  { id: "retina", slug: "sitkivka", title: "Лікування захворювань сітківки", Icon: Sparkles },
  { id: "pediatric", slug: "kids", title: "Дитяча офтальмологія", Icon: Baby },
];

const ServicesSection = () => (
  <section id="services-section" className="py-14 md:py-16 bg-white" data-testid="services-section">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
      <ScrollReveal>
        <div className="mb-10 lg:mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[52px] font-bold text-[#333] leading-[1.05] tracking-tight">
            Наші <span className="text-[#E8590A]">послуги</span>
          </h2>
        </div>
      </ScrollReveal>

      <StaggerContainer className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 lg:gap-5">
        {HOMEPAGE_SERVICES.map((s) => {
          const Icon = s.Icon;
          return (
            <StaggerItem key={s.id}>
              <Link
                to={`/${s.slug}`}
                className="group relative flex flex-col items-start justify-between h-full min-h-[200px] lg:min-h-[230px] bg-white border border-[#E5E5E5] rounded-2xl p-5 lg:p-6 transition-all duration-300 hover:bg-[#E8590A] hover:border-[#E8590A] hover:shadow-[0_12px_40px_-12px_rgba(232,89,10,0.45)] hover:-translate-y-1"
                data-testid={`service-card-${s.id}`}
              >
                <div className="w-12 h-12 rounded-xl bg-[#F5F5F5] group-hover:bg-white/15 flex items-center justify-center transition-colors duration-300">
                  <Icon className="w-6 h-6 text-[#E8590A] group-hover:text-white transition-colors duration-300" strokeWidth={1.75} />
                </div>

                <div className="mt-8">
                  <h3 className="text-[15px] lg:text-base font-semibold text-[#333] group-hover:text-white leading-snug transition-colors duration-300">
                    {s.title}
                  </h3>
                  <span className="mt-3 inline-flex items-center justify-center w-8 h-8 rounded-full border border-[#E5E5E5] group-hover:border-white/60 group-hover:bg-white/10 transition-all duration-300">
                    <ArrowRight className="w-3.5 h-3.5 text-[#333] group-hover:text-white -rotate-45 group-hover:rotate-0 transition-all duration-300" strokeWidth={2} />
                  </span>
                </div>
              </Link>
            </StaggerItem>
          );
        })}
      </StaggerContainer>
    </div>
  </section>
);

// ─── Why Choose Us ───
const WhyChooseSection = () => {
  const { open: openAppointment } = useAppointment();
  return (
  <section className="py-14 md:py-20 bg-[#F5F5F5]" data-testid="why-choose-section">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <ScrollReveal direction="left">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[52px] font-bold text-[#333] leading-[1.05] tracking-tight mb-6">
            Чому обирають <span className="text-[#E8590A]">Центр мікрохірургії ока «Ваш зір»</span>
          </h2>
          <p className="text-base text-[#666] leading-relaxed mb-8 max-w-[520px]">
            Наш центр — це унікальне поєднання найвищої кваліфікації лікарів, обладнання світового рівня та моральних принципів організації. Ми проводимо діагностику та лікування найскладніших захворювань ока на експертному рівні.
          </p>
          <button
            onClick={openAppointment}
            className="inline-flex items-center gap-2 bg-[#E8590A] text-white px-7 py-3.5 rounded-full hover:bg-[#D14D08] transition-all duration-300 font-semibold text-base"
            data-testid="why-choose-appointment-btn"
          >
            Записатись на консультацію <ArrowRight className="w-4 h-4" />
          </button>
        </ScrollReveal>

        <ScrollReveal direction="right">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 lg:gap-5">
            {ADVANTAGES.map((adv, i) => {
              const Icon = iconMap[adv.icon] || Award;
              return (
                <motion.div
                  key={i}
                  className="group relative bg-white rounded-2xl p-6 lg:p-7 border border-[#E5E5E5] transition-all duration-300 hover:border-[#E8590A] hover:shadow-[0_12px_40px_-12px_rgba(232,89,10,0.25)] hover:-translate-y-1"
                  whileHover={{ scale: 1.01 }}
                >
                  <div className="w-11 h-11 rounded-xl bg-[#FFF3ED] flex items-center justify-center mb-6 group-hover:bg-[#E8590A] transition-colors duration-300">
                    <Icon className="w-5 h-5 text-[#E8590A] group-hover:text-white transition-colors duration-300" strokeWidth={1.75} />
                  </div>
                  <h3 className="text-base lg:text-lg font-semibold text-[#333] mb-2 leading-snug">{adv.title}</h3>
                  <p className="text-sm text-[#808080] leading-relaxed">{adv.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </ScrollReveal>
      </div>
    </div>
  </section>
  );
};

// ─── Equipment Section ───
const EquipmentSection = () => (
  <section className="py-24 bg-[#F8F8F6]" data-testid="equipment-section">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <ScrollReveal>
        <div className="text-center mb-16">
          <span className="text-xs font-sans tracking-[0.1em] uppercase text-[#C03A2B] font-semibold">Технології</span>
          <h2 className="text-3xl sm:text-4xl font-serif tracking-tight mt-3">Обладнання світового рівня</h2>
          <p className="font-sans text-[#8C8C8C] mt-4 max-w-xl mx-auto">Єдині в регіоні — найсучасніше обладнання для точної діагностики та ефективного лікування</p>
        </div>
      </ScrollReveal>
      <StaggerContainer className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[
          { img: IMAGES.equipment1, title: "Діагностичне обладнання", desc: "Оптична когерентна томографія, комп'ютерна периметрія", alt: "Діагностичне обладнання офтальмологічного центру «Ваш зір» у Чернівцях — ОКТ, комп'ютерна периметрія" },
          { img: IMAGES.equipment2, title: "Хірургічне обладнання", desc: "Факоемульсифікатор для безшовних операцій з катаракти", alt: "Хірургічне обладнання для операції катаракти у Чернівцях — факоемульсифікатор" },
          { img: IMAGES.equipment3, title: "Лазерне обладнання", desc: "Лазерна коагуляція сітківки, YAG-лазер", alt: "Лазерне обладнання для лікування глаукоми та сітківки у Чернівцях — YAG-лазер" },
        ].map((item, i) => (
          <StaggerItem key={i}>
            <div className="group rounded-2xl overflow-hidden bg-white border border-[#FAF0EE]">
              <div className="aspect-[4/3] overflow-hidden">
                <img src={item.img} alt={item.alt} width="800" height="600" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
              </div>
              <div className="p-6">
                <h3 className="font-serif text-lg mb-2">{item.title}</h3>
                <p className="font-sans text-sm text-[#8C8C8C]">{item.desc}</p>
              </div>
            </div>
          </StaggerItem>
        ))}
      </StaggerContainer>
    </div>
  </section>
);

// ─── Doctors Section ───
const pluralYears = (n) => {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "рік";
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return "роки";
  return "років";
};

const DoctorsSection = () => {
  // Carousel through ALL doctors in the featured slot
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % DOCTORS.length);
    }, 5000);
    return () => clearInterval(t);
  }, []);
  const featured = DOCTORS[idx];

  return (
    <section id="doctors-section" className="py-14 md:py-20 bg-white overflow-hidden" data-testid="doctors-section">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* ── Top: Headline + Featured Doctor (carousel) ── */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-center mb-10 lg:mb-14">
          <ScrollReveal direction="left">
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[52px] font-bold leading-[1.05] tracking-tight">
                <span className="text-[#333]">В нас працюють</span>
                <br />
                <span className="text-[#E8590A]">кращі лікарі регіону</span>
              </h2>

              {/* Orange plus decoration */}
              <svg className="absolute -top-5 right-8 lg:right-24 w-6 h-6 text-[#E8590A] hidden md:block" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M11 2h2v9h9v2h-9v9h-2v-9H2v-2h9V2z" />
              </svg>

              <div className="mt-6 lg:mt-8 flex items-start gap-4 max-w-[520px]">
                <div className="w-12 h-px bg-[#CFCFCF] mt-3 flex-shrink-0" />
                <p className="text-base text-[#666] leading-relaxed">
                  Всі вони мають великий стаж та досвід роботи, а отже є справжніми майстрами своєї справи
                </p>
              </div>
            </div>
          </ScrollReveal>

          <div className="mx-auto lg:mx-0 w-fit">
            {/* Photo wrapper — rings sized to photo */}
            <div className="relative w-[240px] h-[300px] sm:w-[300px] sm:h-[380px] lg:w-[340px] lg:h-[440px] mx-auto">
              {/* Orange accent rings */}
              <div className="absolute -inset-3 rounded-full border-2 border-[#E8590A]/35 pointer-events-none" />
              <div className="absolute -inset-7 rounded-full border border-[#E8590A]/15 pointer-events-none" />

              {/* Oval photo with crossfade */}
              <div className="relative w-full h-full rounded-full overflow-hidden bg-white shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={featured.id}
                    src={featured.photo}
                    alt={`${featured.name} — офтальмолог у Чернівцях, ${featured.specialization}, Центр «Ваш зір»`}
                    width="340"
                    height="440"
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover object-[center_15%]"
                    initial={{ opacity: 0, scale: 1.04 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.98 }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  />
                </AnimatePresence>
              </div>

              {/* Desktop name pill — smaller, positioned at bottom edge of photo */}
              <div className="hidden lg:block absolute -bottom-4 -right-4 lg:-right-10 max-w-[250px]">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={featured.id + "-pill"}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_12px_40px_-12px_rgba(0,0,0,0.18)] px-4 py-3.5"
                  >
                    <h3 className="text-sm font-semibold text-[#333] leading-snug">
                      {featured.name}
                    </h3>
                    <p className="text-[12px] text-[#808080] mt-1 leading-snug line-clamp-3">
                      {featured.role}
                    </p>
                    <div className="inline-flex items-center gap-1.5 mt-2 text-[11px] font-semibold text-[#E8590A]" data-testid={`doctor-experience-${featured.id}`}>
                      <Clock className="w-3 h-3" strokeWidth={2.25} />
                      {featured.experience} {pluralYears(featured.experience)} досвіду
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>

            {/* Mobile: compact name card below photo */}
            <div className="lg:hidden mt-10 mx-auto max-w-[280px]">
              <AnimatePresence mode="wait">
                <motion.div
                  key={featured.id + "-mob"}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white rounded-2xl shadow-[0_12px_40px_-12px_rgba(0,0,0,0.12)] border border-[#F0F0F0] p-4"
                >
                  <h3 className="text-[15px] font-semibold text-[#333] leading-snug">
                    {featured.name}
                  </h3>
                  <p className="text-[13px] text-[#808080] mt-1 leading-snug">
                    {featured.role}
                  </p>
                  <div className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-[#E8590A]">
                    <Clock className="w-3.5 h-3.5" strokeWidth={2.25} />
                    {featured.experience} {pluralYears(featured.experience)} досвіду
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Pagination dots */}
            <div className="flex justify-center gap-1.5 mt-8" data-testid="doctors-carousel-dots">
              {DOCTORS.map((d, i) => (
                <button
                  key={d.id}
                  onClick={() => setIdx(i)}
                  aria-label={`Показати ${d.name}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-6 bg-[#E8590A]" : "w-1.5 bg-[#E0E0E0] hover:bg-[#999]"}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ── Bottom: Grid of ALL doctors (4 per row) ── */}
        <StaggerContainer className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5 lg:gap-6">
          {DOCTORS.map((doc) => (
            <StaggerItem key={doc.id} className="h-full">
              <Link
                to={DOCTOR_PAGES[doc.id] ? `/doctors/${doc.id}` : "/doctors"}
                className="group block relative h-full"
                data-testid={`doctor-card-${doc.id}`}
              >
                {/* Orange bottom slice peeking under the card */}
                <div className="absolute left-5 right-5 bottom-0 h-7 bg-[#E8590A] rounded-b-3xl" />

                <div className="relative bg-white rounded-3xl border border-[#EDEDED] overflow-hidden transition-all duration-300 group-hover:-translate-y-2 group-hover:shadow-[0_24px_60px_-20px_rgba(232,89,10,0.35)] h-full flex flex-col">
                  {/* Photo */}
                  <div className="relative aspect-[4/5] bg-[#F5F5F5] overflow-hidden flex-shrink-0">
                    <img
                      src={doc.photoSm || doc.photo}
                      alt={`${doc.name} — офтальмолог у Чернівцях, ${doc.specialization}`}
                      width="480"
                      height="600"
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>

                  {/* Name plate — smaller, positioned lower to not cover face */}
                  <div className="relative -mt-5 mx-4 mb-5 bg-white rounded-2xl shadow-[0_8px_30px_-12px_rgba(0,0,0,0.15)] border border-[#EFEFEF] px-3 py-3 text-center flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className="text-[13px] lg:text-[14px] font-semibold text-[#333] leading-tight">
                        {doc.name}
                      </h3>
                      <p className="text-[11px] lg:text-[12px] text-[#808080] mt-1.5 leading-snug line-clamp-3">
                        {doc.role}
                      </p>
                    </div>
                    <div className="inline-flex items-center justify-center gap-1 mt-2 text-[10px] lg:text-[11px] font-semibold text-[#E8590A]">
                      <Clock className="w-3 h-3" strokeWidth={2.25} />
                      {doc.experience} {pluralYears(doc.experience)} досвіду
                    </div>
                  </div>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
};

// ─── Self Test Section ───
const SelfTestSection = () => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [finished, setFinished] = useState(false);

  const handleAnswer = (weightIdx) => {
    const newAnswers = [...answers, SELF_TEST_QUESTIONS[step].weights[weightIdx]];
    setAnswers(newAnswers);
    if (step + 1 < SELF_TEST_QUESTIONS.length) {
      setStep(step + 1);
    } else {
      setFinished(true);
    }
  };

  const totalScore = answers.reduce((a, b) => a + b, 0);
  const needsConsultation = totalScore >= 5;
  const progress = ((step + (finished ? 1 : 0)) / SELF_TEST_QUESTIONS.length) * 100;

  const reset = () => { setStep(0); setAnswers([]); setFinished(false); };

  return (
    <section className="py-24 bg-[#FAF0EE]" data-testid="self-test-section">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-10">
            <span className="text-xs font-sans tracking-[0.1em] uppercase text-[#C03A2B] font-semibold">Тест</span>
            <h2 className="text-3xl sm:text-4xl font-serif tracking-tight mt-3">Чи потрібна вам консультація?</h2>
            <p className="font-sans text-[#8C8C8C] mt-3">Дайте відповідь на 5 питань</p>
          </div>
        </ScrollReveal>

        <div className="bg-white rounded-3xl p-8 md:p-12">
          {/* Progress bar */}
          <div className="w-full h-1.5 bg-[#FAF0EE] rounded-full mb-8 overflow-hidden">
            <motion.div
              className="h-full bg-[#C03A2B] rounded-full"
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>

          {!finished ? (
            <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.3 }}>
              <p className="font-sans text-xs text-[#8C8C8C] mb-2">Питання {step + 1} з {SELF_TEST_QUESTIONS.length}</p>
              <h3 className="text-xl font-serif mb-8">{SELF_TEST_QUESTIONS[step].question}</h3>
              <div className="space-y-3">
                {SELF_TEST_QUESTIONS[step].options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => handleAnswer(i)}
                    className="w-full text-left p-4 rounded-xl border border-[#FAF0EE] font-sans hover:border-[#C03A2B] hover:bg-[#FAF0EE] transition-all"
                    data-testid={`test-option-${step}-${i}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center">
              <div className={`w-20 h-20 rounded-full mx-auto mb-6 flex items-center justify-center ${needsConsultation ? "bg-[#C03A2B]" : "bg-[#FAF0EE]"}`}>
                <Eye className={`w-10 h-10 ${needsConsultation ? "text-white" : "text-[#C03A2B]"}`} strokeWidth={1.5} />
              </div>
              <h3 className="text-2xl font-serif mb-4">
                {needsConsultation ? "Рекомендуємо записатись на консультацію" : "Ваш зір в нормі!"}
              </h3>
              <p className="font-sans text-[#8C8C8C] mb-8">
                {needsConsultation
                  ? "За результатами тесту, вам варто відвідати офтальмолога для перевірки зору."
                  : "Але не забувайте про регулярні профілактичні огляди."}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="#appointment" className="bg-[#C03A2B] text-white font-sans px-8 py-3 rounded-full hover:bg-[#8B1F1F] transition-colors font-medium" data-testid="test-appointment-btn">
                  Записатись на прийом
                </a>
                <button onClick={reset} className="font-sans text-[#8C8C8C] hover:text-[#1C1C1E] transition-colors" data-testid="test-reset-btn">
                  Пройти ще раз
                </button>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};

// ─── Reviews Section ───
const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    axios.get(`${API}/reviews`).then((r) => setReviews(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (reviews.length === 0) return;
    const timer = setInterval(() => setCurrent((c) => (c + 1) % reviews.length), 5000);
    return () => clearInterval(timer);
  }, [reviews.length]);

  if (reviews.length === 0) return null;

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="reviews-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <ScrollReveal>
          <div className="text-center mb-16">
            <span className="text-xs font-sans tracking-[0.1em] uppercase text-[#C03A2B] font-semibold">Відгуки</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight mt-3">Що кажуть наші пацієнти</h2>
          </div>
        </ScrollReveal>
        <div className="relative min-h-[200px]">
          {reviews.map((review, i) => (
            <motion.div
              key={review.id}
              initial={false}
              animate={{ opacity: i === current ? 1 : 0, scale: i === current ? 1 : 0.95 }}
              transition={{ duration: 0.5 }}
              className={`text-center ${i === current ? "" : "absolute inset-0 pointer-events-none"}`}
            >
              <div className="flex justify-center gap-1 mb-6">
                {Array.from({ length: 5 }).map((_, si) => (
                  <Star key={si} className={`w-5 h-5 ${si < review.rating ? "text-[#C03A2B] fill-[#C03A2B]" : "text-gray-200"}`} />
                ))}
              </div>
              <p className="text-lg font-sans text-[#1C1C1E] leading-relaxed mb-6 max-w-2xl mx-auto italic">"{review.text}"</p>
              <p className="font-sans font-medium text-[#1C1C1E]">{review.name}</p>
              <p className="font-sans text-sm text-[#8C8C8C]">{review.service}</p>
            </motion.div>
          ))}
        </div>
        <div className="flex justify-center gap-2 mt-8">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-2 h-2 rounded-full transition-all ${i === current ? "bg-[#C03A2B] w-8" : "bg-[#FAF0EE]"}`}
              aria-label={`Review ${i + 1}`}
            />
          ))}
        </div>
        <div className="text-center mt-8">
          <Link to="/reviews" className="font-sans text-[#C03A2B] font-medium hover:text-[#8B1F1F] transition-colors inline-flex items-center gap-2" data-testid="more-reviews-link">
            Більше відгуків <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// ─── Contacts Section ───
const CONTACT_QUERY = encodeURIComponent("Чернівці, вул. Вокзальна, 21 Б");
const DIRECTIONS_DRIVING = `https://www.google.com/maps/dir/?api=1&destination=${CONTACT_QUERY}&travelmode=driving`;
const DIRECTIONS_TRANSIT = `https://www.google.com/maps/dir/?api=1&destination=${CONTACT_QUERY}&travelmode=transit`;

// ─── FAQ Section (data) ───
const HOME_FAQ = [
  {
    q: "Де знаходиться Центр мікрохірургії ока «Ваш зір» в Чернівцях?",
    a: "Центр розташований за адресою вул. Вокзальна, 21Б, Чернівці, навпроти залізничного вокзалу — дістатися можна громадським транспортом з будь-якого району міста.",
  },
  {
    q: "Чи потрібне направлення від сімейного лікаря для запису?",
    a: "Ні, ви можете записатися на консультацію напряму, без направлення. Достатньо зателефонувати або залишити заявку на сайті.",
  },
  {
    q: "Яке обладнання використовує центр?",
    a: "Центр оснащений сучасним діагностичним і хірургічним обладнанням світового рівня, що дозволяє проводити точну діагностику та малоінвазивні операції.",
  },
  {
    q: "Скільки коштує первинна консультація офтальмолога в Чернівцях?",
    a: "Вартість консультації уточнюйте за телефоном — вона залежить від типу обстеження, яке вам необхідне.",
  },
  {
    q: "Чи є знижки для пенсіонерів?",
    a: "Так, у центрі діють спеціальні умови для пенсіонерів. Деталі уточнюйте під час запису.",
  },
  {
    q: "Чому варто обрати Центр мікрохірургії ока «Ваш зір» серед інших клінік Чернівців?",
    a: "Понад 30 років досвіду, 60 000+ проведених операцій та команда лікарів вищої категорії — саме тому пацієнти з Чернівців та області довіряють нам своє здоров'я.",
  },
];

// ─── Contacts Section ───
const ContactsSection = () => (
  <section id="contacts" className="py-14 md:py-20 bg-white" data-testid="contacts-section">
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
      <ScrollReveal>
        <div className="text-center mb-10 lg:mb-12 max-w-2xl mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[52px] font-bold text-[#333] leading-[1.05] tracking-tight">
            Наші <span className="text-[#E8590A]">контакти</span>
          </h2>
          <p className="text-base text-[#666] mt-3">
            Завжди раді вас бачити у нашому центрі — ось як до нас дістатися
          </p>
        </div>
      </ScrollReveal>

      <div className="grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-5 lg:gap-6">
        {/* Left info card */}
        <ScrollReveal direction="left">
          <div className="bg-[#F5F5F5] rounded-3xl p-7 lg:p-8 h-full flex flex-col">
            <h3 className="text-2xl lg:text-[26px] font-bold text-[#333] leading-tight mb-8">
              <span className="text-[#E8590A]">Центр мікрохірургії ока «Ваш зір»</span>
            </h3>

            <div className="space-y-6">
              {/* Address */}
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#E8590A]" strokeWidth={2} />
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-[#999] mb-1.5">Адреса</h4>
                  <p className="text-[15px] text-[#333] leading-snug">{CONTACT.address}</p>
                </div>
              </div>

              {/* Phones */}
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#E8590A]" strokeWidth={2} />
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-[#999] mb-1.5">Телефони</h4>
                  <PhoneLink href={CONTACT.phone1Link} className="block text-[15px] text-[#333] hover:text-[#E8590A] transition-colors font-medium">{CONTACT.phone1}</PhoneLink>
                  <PhoneLink href={CONTACT.phone2Link} className="block text-[15px] text-[#333] hover:text-[#E8590A] transition-colors font-medium">{CONTACT.phone2}</PhoneLink>
                  <PhoneLink href={CONTACT.phone3Link} className="block text-[15px] text-[#333] hover:text-[#E8590A] transition-colors font-medium">{CONTACT.phone3}</PhoneLink>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-xl bg-white flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#E8590A]" strokeWidth={2} />
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-[#999] mb-1.5">Години роботи</h4>
                  <p className="text-[15px] text-[#333] leading-snug">{CONTACT.workHours}</p>
                </div>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Right: map */}
        <ScrollReveal direction="right">
          <div className="relative rounded-3xl overflow-hidden border border-[#EDEDED] h-[380px] sm:h-[420px] lg:h-[440px]">
            <iframe
              src={CONTACT.mapUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Google Maps"
              className="absolute inset-0"
            />
          </div>
        </ScrollReveal>
      </div>
    </div>
  </section>
);

// ─── Appointment Section ───
const AppointmentSection = () => (
  <section id="appointment" className="py-24 md:py-32 bg-white" data-testid="appointment-section">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <ScrollReveal>
        <div className="text-center mb-12">
          <span className="text-xs font-sans tracking-[0.1em] uppercase text-[#C03A2B] font-semibold">Запис</span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight mt-3">Запишіться на консультацію</h2>
          <p className="font-sans text-[#8C8C8C] mt-4">Ми зв'яжемося з вами протягом 30 хвилин</p>
        </div>
      </ScrollReveal>
      <ScrollReveal delay={0.2}>
        <AppointmentForm />
      </ScrollReveal>
    </div>
  </section>
);

// ─── Home Page ───
export default function HomePage() {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <ServicesSection />
      <IshiharaTestSection />
      <DoctorsSection />
      <ReviewsMarquee />
      <WhyChooseSection />
      <FaqSection items={HOME_FAQ} />
      <ContactsSection />
    </div>
  );
}
